import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import {
  EventStaffPrimary,
  TEventsApiResponse,
  TEventsPrimary,
  TEventsMonthWindowFlattened,
} from '../../../../apis/events/events.interfaces';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import { SECTION } from '../../../../apis/admissions/admissions.interfaces';
import { useEventsListHook } from '../../events/hooks/useEventsListHook';
import { GRADE } from '../../../../apis/admissions/admissions.interfaces';
import {
  GENDER,
  TUserDetailsSimplified,
} from '../../../../apis/users/users.interfaces';
import {
  useStaffEventsHook,
  TStaffMonthWindowParams,
} from '../hooks/useStaffEventsHook';
import { EventGroup, EventScope } from '../../../../apis/events/event.enums';

type TStaffEventsAppState = {
  errorMessage?: string;
  isLoading: boolean;
  requestParams: TPaginationParams;
  eventsList: TEventsPrimary[];
};

enum ACTION_TYPE {
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_EVENTS = 'UPDATE_EVENTS',
  UPDATE_EVENTS_LIST = 'UPDATE_EVENTS_LIST',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.UPDATE_EVENTS; payload: TEventsPrimary[] }
  | { type: ACTION_TYPE.UPDATE_EVENTS_LIST; payload: TEventsPrimary[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialStaffEventsState: TStaffEventsAppState = {
  eventsList: [],
  errorMessage: undefined,
  isLoading: false,
  requestParams: {
    page: 1,
    limit: 10,
  },
};

type TState = {
  staffEventsAppState: TStaffEventsAppState;
  staffEventsResponse: TEventsApiResponse | TEventsMonthWindowFlattened;
  simplifiedStaffObject: Record<string, TUserDetailsSimplified>;
  filteredEvents: TEventsPrimary[];
};

const StaffEventsContext = createContext<TState | undefined>(undefined);
const StaffEventsDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const StaffEventsReducer = (
  state: TStaffEventsAppState,
  action: Action
): TStaffEventsAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...state,
        requestParams: { ...state.requestParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_EVENTS:
      return { ...state, ...action.payload };
    case ACTION_TYPE.UPDATE_EVENTS_LIST:
      return { ...state, eventsList: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StaffEventsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [staffEventsAppState, dispatch] = useReducer(
    StaffEventsReducer,
    initialStaffEventsState
  );
  const simplifiedStaffObject = useMemo<Record<string, TUserDetailsSimplified>>(
    () => ({
      '9027555614': {
        id: '9027555614',
        grade: GRADE.ELEVEN,
        section: SECTION.C,
        basicDetails: {
          firstName: 'Test',
          lastName: 'User',
          gender: GENDER.MALE,
        },
      },
      '123456789': {
        id: '123456789',
        grade: GRADE.EIGHT,
        section: SECTION.A,
        basicDetails: {
          firstName: 'John',
          lastName: 'Smith',
          gender: GENDER.MALE,
        },
      },
      '987654321': {
        id: '987654321',
        grade: GRADE.EIGHT,
        section: SECTION.B,
        basicDetails: {
          firstName: 'Jane',
          lastName: 'Doe',
          gender: GENDER.FEMALE,
        },
      },
    }),
    []
  );

  // instituteId is now extracted from JWT on the backend
  const monthWindowParams: TStaffMonthWindowParams = {
    ...staffEventsAppState.requestParams,
    currentMonth: new Date().toISOString().slice(0, 7),
    includeSpanning: true,
    mode: 'window',
  };

  const {
    data: staffEventsResponse,
    isLoading: staffEventsResponseLoading,
    isError: staffEventsResponseLoadingError,
    isSuccess: staffEventsResponseLoadingDataSuccess,
    error: staffEventsResponseDataError,
  } = useStaffEventsHook(monthWindowParams);

  useEffect(() => {
    if (staffEventsResponseLoading) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (staffEventsResponseLoadingDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (staffEventsResponseLoadingError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload:
          staffEventsResponseDataError?.response?.data.message ??
          ERROR_MESSAGE.GET_EVENTS_FAILURE,
      });
    }
    return () => dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
  }, [
    staffEventsResponseLoading,
    staffEventsResponseLoadingDataSuccess,
    staffEventsResponseLoadingError,
    staffEventsResponseDataError,
  ]);

  const { data: eventsListData } = useEventsListHook({
    page: 1,
    limit: 100,
  });

  const filteredEvents = useMemo<TEventsPrimary[]>(() => {
    const sourceEvents =
      Array.isArray(staffEventsResponse?.data) &&
      staffEventsResponse?.data.length
        ? staffEventsResponse.data
        : eventsListData?.data || [];

    const mappedEvents = sourceEvents.map((event: any) => {
      // If event already has eventScopes (from API normalization), use it
      // Otherwise normalize from eventScope (for mock data)
      const normalizedScopes =
        event.eventScopes &&
        Array.isArray(event.eventScopes) &&
        event.eventScopes.length > 0
          ? event.eventScopes
          : Array.isArray(event.eventScope)
            ? event.eventScope
                .flat(Infinity)
                .filter((s: any) => s && typeof s === 'string')
            : [event.eventScope].filter(Boolean);

      return {
        id: event.id,
        title: event.title,
        manager: event.manager,
        description: event.description,
        startDate: event.startDate,
        endDate: event.endDate,
        eventGroup: event.eventGroup as EventGroup,
        eventScope: (normalizedScopes[0] ||
          event.eventScope ||
          'institute') as EventScope,
        eventScopes: normalizedScopes as EventScope[],
        expenditureActual: event.expenditureActual,
        expenditureEstimate: event.expenditureEstimate,
        institute: event.institute,
        plannedBy: event.plannedBy,
        eventDate: new Date(event.startDate),
        participants: (event.participants || []).map((p: any) => ({
          type: 'student' as const,
          id: p.id,
          isFeeRequired: false,
          status: 'confirmed' as const,
          attendanceStatus: 'present' as const,
        })),
        staffAssignments: (event.staffAssignments || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          email: s.email,
          eventRole: s?.eventRole,
          responsibilities: s?.responsibilities,
        })) as EventStaffPrimary[],
        purpose: event.purpose,
        remarks: event.remarks,
        venue: event.venue,
        priority: 'medium' as const,
        isMandatory: true,
        status: (event.status === 'scheduled' ? 'scheduled' : 'completed') as
          | 'scheduled'
          | 'completed'
          | 'cancelled',
        relatedClass: {
          grade: '11' as GRADE,
          section: 'C' as SECTION,
        },
      };
    });

    return mappedEvents.filter((event) => {
      // Filter by scope: must include 'staff'
      const scopes =
        event.eventScopes && event.eventScopes.length > 0
          ? event.eventScopes
          : [event.eventScope].filter(Boolean);

      const hasStaffScope = scopes.some(
        (scope: any) =>
          scope && typeof scope === 'string' && scope.toLowerCase() === 'staff'
      );

      if (!hasStaffScope) {
        return false;
      }

      // If event has staff scope, include it regardless of assignments
      // (The hardcoded simplifiedStaffObject is just mock data)
      return true;
    }) as TEventsPrimary[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simplifiedStaffObject, staffEventsResponse, eventsListData]);

  const currentState = useMemo<TState>(
    () => ({
      staffEventsAppState,
      staffEventsResponse: (staffEventsResponse as any) ?? {
        data: [],
        total: 0,
      },
      simplifiedStaffObject,
      filteredEvents,
    }),
    [
      staffEventsAppState,
      staffEventsResponse,
      filteredEvents,
      simplifiedStaffObject,
    ]
  );

  return (
    <StaffEventsDispatchContext.Provider value={dispatch}>
      <StaffEventsContext.Provider value={currentState}>
        {children}
      </StaffEventsContext.Provider>
    </StaffEventsDispatchContext.Provider>
  );
};

export const useStaffEventsState = () => {
  const state = useContext(StaffEventsContext);
  if (state === undefined) {
    throw new Error(
      'useStaffEventsState must be used within a StaffEventsProvider'
    );
  }
  return state;
};

export const useStaffEventsActions = () => {
  const dispatch = useContext(StaffEventsDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStaffEventsActions dispatch must be used within a StaffEventsProvider'
    );
  }
  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };
  const updateEventsList = (list: TEventsPrimary[]) => {
    dispatch({ type: ACTION_TYPE.UPDATE_EVENTS_LIST, payload: list });
  };
  return { updateSearchParams, updateEventsList };
};
